'use client';

import * as React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getUserProfile, UserProfile } from '../../lib/admin';
import { useAdminContext } from '../../contexts/AdminContext';
import { cn } from '../../lib/utils';

interface UserProfileDropdownProps {
  className?: string;
}

export function UserProfileDropdown({ className }: UserProfileDropdownProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [profile, setProfile] = React.useState<UserProfile | null>(null);
  const [loading, setLoading] = React.useState(true);
  const { user, signOut } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminContext();
  const navigate = useNavigate();
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Carregar perfil do usuário
  React.useEffect(() => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'user-profile-dropdown.tsx:25',message:'useEffect disparado para carregar perfil',data:{userId:user?.id},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H5',runId:'post-fix'})}).catch(()=>{});
    // #endregion
    if (user?.id) {
      getUserProfile(user.id).then((userProfile) => {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'user-profile-dropdown.tsx:28',message:'Perfil carregado no dropdown',data:{codesCopied:userProfile?.codes_copied_count,cardsCreated:userProfile?.cards_created_count},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H5',runId:'post-fix'})}).catch(()=>{});
        // #endregion
        setProfile(userProfile);
        setLoading(false);
      }).catch(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [user]);

  // Fechar dropdown ao clicar fora
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Abreviar email
  const abbreviateEmail = (email: string): string => {
    if (!email) return 'Usuário';
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 15) {
      return email;
    }
    return `${localPart.substring(0, 15)}...`;
  };

  const handleLogout = async () => {
    setIsOpen(false);
    await signOut();
    window.location.href = '/login';
  };

  if (loading || !user) {
    return (
      <div className={cn("flex items-center space-x-2", className)}>
        <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
      </div>
    );
  }

  const displayName = user.email ? abbreviateEmail(user.email) : 'Usuário';
  const codesCopied = profile?.codes_copied_count || 0;
  const cardsCreated = profile?.cards_created_count || 0;
  const points = profile?.points || 0;
  const level = profile?.level || 1;

  // Recarregar perfil quando abrir o dropdown
  const handleOpenDropdown = () => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'user-profile-dropdown.tsx:85',message:'Dropdown aberto, recarregando perfil',data:{userId:user?.id,wasOpen:isOpen},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H5',runId:'post-fix'})}).catch(()=>{});
    // #endregion
    setIsOpen(!isOpen);
    
    // Recarregar perfil ao abrir (não ao fechar)
    if (!isOpen && user?.id) {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'user-profile-dropdown.tsx:92',message:'Recarregando perfil do usuário',data:{userId:user.id},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H5',runId:'post-fix'})}).catch(()=>{});
      // #endregion
      getUserProfile(user.id).then((userProfile) => {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'user-profile-dropdown.tsx:95',message:'Perfil recarregado ao abrir dropdown',data:{codesCopied:userProfile?.codes_copied_count,cardsCreated:userProfile?.cards_created_count},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H5',runId:'post-fix'})}).catch(()=>{});
        // #endregion
        setProfile(userProfile);
      }).catch((err) => {
        console.error('Erro ao recarregar perfil:', err);
      });
    }
  };

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      {/* Botão do perfil */}
      <button
        onClick={handleOpenDropdown}
        className="flex items-center space-x-2 px-3 py-2 rounded-full bg-white/10 dark:bg-white/5 hover:bg-white/20 dark:hover:bg-white/10 transition-all duration-200 border border-transparent hover:border-[#6a0dad]/30 dark:hover:border-[#8a2be2]/30"
      >
        {/* Avatar */}
        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#6a0dad] to-[#8a2be2] flex items-center justify-center text-white font-semibold text-sm shadow-lg">
          <i className="ri-user-line text-lg"></i>
        </div>
        
        {/* Nome e stats */}
        <div className="hidden md:flex flex-col items-start">
          <span className="text-sm font-medium text-gray-900 dark:text-[#e0e1dd] font-ubuntu">
            {displayName}
          </span>
          <span className="text-xs text-gray-500 dark:text-[#c780ff] font-montserrat">
            {codesCopied.toLocaleString('pt-BR')} códigos copiados
          </span>
        </div>

        {/* Ícone de dropdown */}
        <i className={cn(
          "ri-arrow-down-s-line text-gray-600 dark:text-[#c780ff] transition-transform duration-200",
          isOpen && "transform rotate-180"
        )}></i>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-80 bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d] rounded-xl shadow-2xl border border-gray-200 dark:border-[#8a2be2]/30 z-50 overflow-hidden"
          >
            {/* Header do dropdown */}
            <div className="p-4 bg-gradient-to-r from-[#6a0dad] to-[#8a2be2]">
              <div className="flex items-center space-x-3">
                <div className="h-12 w-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white">
                  <i className="ri-user-line text-2xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate font-ubuntu">
                    {user.email || 'Usuário'}
                  </p>
                  <p className="text-white/80 text-xs font-montserrat">
                    Nível {level} • {points.toLocaleString('pt-BR')} pontos
                  </p>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="p-4 border-b border-gray-200 dark:border-[#8a2be2]/30">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 dark:bg-[#0d0d0d]/50 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <i className="ri-file-copy-line text-[#415a77] dark:text-[#778da9]"></i>
                    <span className="text-xs text-gray-600 dark:text-[#778da9] font-montserrat">Códigos Copiados</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">
                    {codesCopied.toLocaleString('pt-BR')}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-[#0d0d0d]/50 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <i className="ri-file-add-line text-[#415a77] dark:text-[#778da9]"></i>
                    <span className="text-xs text-gray-600 dark:text-[#778da9] font-montserrat">Cards Criados</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">
                    {cardsCreated.toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>
            </div>

            {/* Menu items */}
            <div className="p-2 space-y-1">
              {!adminLoading && isAdmin && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsOpen(false);
                    navigate('/admin/users');
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg bg-gradient-to-r from-[#6a0dad] to-[#8a2be2] hover:from-[#8a2be2] hover:to-[#c780ff] text-white font-medium transition-all duration-200 text-left shadow-sm"
                >
                  <i className="ri-admin-line text-lg"></i>
                  <span className="font-medium font-montserrat">Admin</span>
                </button>
              )}
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/ranking');
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-[#415a77] dark:text-[#778da9] hover:bg-gray-50 dark:hover:bg-[#0d1b2a]/30 transition-colors duration-200 text-left"
              >
                <i className="ri-trophy-line text-lg"></i>
                <span className="font-medium font-montserrat">Ver Ranking</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200 text-left"
              >
                <i className="ri-logout-box-line text-lg"></i>
                <span className="font-medium font-montserrat">Sair</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
